import {WebSocketServerConnection} from '../connection-types';
import {IServerCommand, ConnectionState, IServerResponse, TimeoutConfigCallback, IRetryInfo, ServerMessageType} from '../types';

const XML_KEEP_ALIVE_COMMAND: IServerCommand = {
    toPayload() {
        return '<ping />';
    }
};
const KEEP_ALIVE_RESPONSE = '<pong />';

const JSON_KEEP_ALIVE_COMMAND: IServerCommand = {
    toPayload() {
        return JSON.stringify({ type: 'ping', payload: null });
    }
};

export class WebSocketKeepAlive {
    private keepAliveIntervalId?: number;
    private connectionLostIntervalId?: number;
    private connectionLostDelayTimerId?: number;
    private isAlive: boolean = false;

    constructor(
        private readonly webSocket: WebSocketServerConnection,
        private readonly keepAliveConfigCallback: TimeoutConfigCallback,
        private readonly connectionLostConfigCallback: TimeoutConfigCallback,
        private readonly format: ServerMessageType = 'xml'
      ) {
        this.webSocket = webSocket;
        this.webSocket.stream$.subscribe((payload) => this.setIsAlive(payload));
    }

    public start(retryInfo?: IRetryInfo) {
        this.cancelConnectionLostInterval();
        this.cancelKeepAliveInterval();

        const keepAliveTimeoutMilliseconds = this.keepAliveConfigCallback(this.webSocket, retryInfo);
        const connectionLostTimeoutMilliseconds = this.connectionLostConfigCallback(this.webSocket, retryInfo);
        const connectionLostDelayTimeout = keepAliveTimeoutMilliseconds * 1.5;
        this.keepAliveIntervalId = window.setInterval(() => this.ping(), keepAliveTimeoutMilliseconds);
        this.connectionLostDelayTimerId = window.setTimeout(() => {
            this.connectionLostDelayTimerId = undefined;
            this.isAlive = false;
            this.connectionLostIntervalId = window.setInterval(() => this.detectConnectionLost(), connectionLostTimeoutMilliseconds);
        }, connectionLostDelayTimeout);
        this.log(`keep-alive :: started (keep-alive=${keepAliveTimeoutMilliseconds}ms cnn-lost=${connectionLostTimeoutMilliseconds}ms)`);
    }

    public stop() {
        this.cancelConnectionLostInterval();
        this.cancelKeepAliveInterval();
        this.log('keep-alive :: stopped');
    }

    private ping() {
        if (this.webSocket.connectionState === ConnectionState.CONNECTED) {
            this.webSocket.send(this.format === 'xml' ? XML_KEEP_ALIVE_COMMAND : JSON_KEEP_ALIVE_COMMAND);
            this.logDebug('keep-alive :: ping');
        }
    }

    private detectConnectionLost() {
        if (this.isAlive) {
            this.logDebug('connection :: ok');
            this.isAlive = false;
            return;
        }

        this.stop();
        this.log('connection :: lost');
        this.webSocket.connectionStatus$.next({ STATE: ConnectionState.CONNECTION_LOST });
    }

    private cancelConnectionLostInterval() {
        if (this.connectionLostDelayTimerId) {
            clearTimeout(this.connectionLostDelayTimerId);
            this.connectionLostDelayTimerId = undefined;
        }

        if (this.connectionLostIntervalId) {
            clearInterval(this.connectionLostIntervalId);
            this.connectionLostIntervalId = undefined;
        }
    }

    private cancelKeepAliveInterval() {
        if (this.keepAliveIntervalId) {
            clearInterval(this.keepAliveIntervalId);
            this.keepAliveIntervalId = undefined;
        }
    }

    private setIsAlive(payload: IServerResponse) {
        this.isAlive = true;

        if (payload.Contents === KEEP_ALIVE_RESPONSE ) {
            this.logDebug('connection :: alive');
        }
    }

    private log(message: any): void {
        this.webSocket.log(message);
    }

    private logDebug(message: any): void {
        this.webSocket.logDebug(message);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/server-communications/utilities/web-socket-keep-alive.ts